import { useState, useContext, useEffect } from "react";
import { Card, CardBody, CardHeader, Col, FormGroup, Input, Row, Label, Button } from "reactstrap";
import QuoteOverviewFinancial from "./quoteOverviewFinancial";
import QuoteOverviewGeneral from "./quoteOverviewGeneral";
import { QuoteDataContext } from "../contexts/quoteDataContext";
import QuoteOverviewSystem from "./quoteOverviewSystem";
import QuoteOverviewSalesforceTable from "./quoteOverviewSalesforce";
import { exportDataAsExcel } from "../../importTemplate/data/importTemplateService";
import { toast } from "react-toastify";

const QuoteOverview = ({quote, quoteRevision, quoteItems, salesforceFields}) => {
    const [currentActiveMainTab, setCurrentActiveMainTab] = useState("General");
    const [profitAndLossView, setProfitAndLossView] = useState(true);
    const [exportData, setExportData] = useState({});
    const [quoteItemsWithLinkedData, setQuoteItemsWithLinkedData] = useState([]);
    const [quoteItemsCommercialView, setQuoteItemsCommercialView] = useState([]);
    const [isDownloadingExport, setIsDownloadingExport] = useState(false);

    const quoteDataContext = useContext(QuoteDataContext);
    const { itemLookupTypes, suppliers, salesforceCodes } = quoteDataContext;

    const shouldSpreadValueAcrossYears = (item) => {
        const salesforceCode = salesforceCodes?.find(code => code.name === item.upfrontSalesforceCode);
        if (!salesforceCode) return false;
        return item.isUpfront && item.upfrontRecognitionMonths > 0 && salesforceCode.type === "Recurring" && salesforceCode.canBePaidUpfront;
    }

    const updatedValuesForCommercialView = (item) => {
        const updatedValues = {};
        const termLengthMonths = item.upfrontRecognitionMonths
        const termLengthYears = Math.ceil(termLengthMonths / 12);

        if (shouldSpreadValueAcrossYears(item)) {
            if (termLengthYears >= 1) {
                updatedValues.isUpfront = false;
                updatedValues.isRecurring = true;
                updatedValues.upfrontSalesforceCode = "";
                updatedValues.recurringSalesforceCode = item.upfrontSalesforceCode;
                updatedValues.upfrontBusinessCOE = "";
                updatedValues.recurringBusinessCOE = item.upfrontBusinessCOE;
                updatedValues.upfrontCost = 0;
                updatedValues.upfrontPrice = 0;
                updatedValues.upfrontMargin = 0;
                updatedValues.totalUpfrontCost = 0;
                updatedValues.totalUpfrontPrice = 0;
                updatedValues.totalUpfrontMargin = 0;
                updatedValues.recurringNumPayments = termLengthYears;
                updatedValues.recurringStartMonth = 1;
                updatedValues.recurringIntervalMonths = 12;
                updatedValues.recurringCost = item.upfrontCost / termLengthYears;
                updatedValues.recurringPrice = item.upfrontPrice / termLengthYears;
                updatedValues.recurringMargin = item.upfrontMargin / termLengthYears;
                updatedValues.totalRecurringCost = item.totalUpfrontCost;
                updatedValues.totalRecurringPrice = item.totalUpfrontPrice;
                updatedValues.totalRecurringMargin = item.totalUpfrontMargin;
                updatedValues.annualCost = item.upfrontCost / termLengthYears;
                updatedValues.annualPrice = item.upfrontPrice / termLengthYears;
                updatedValues.annualMargin = item.upfrontMargin / termLengthYears;
                updatedValues.totalAnnualCost = (item.totalUpfrontCost / termLengthMonths) * (termLengthMonths >= 12 ? 12 : termLengthMonths);
                updatedValues.totalAnnualPrice = (item.totalUpfrontPrice / termLengthMonths) * (termLengthMonths >= 12 ? 12 : termLengthMonths);
                updatedValues.totalAnnualMargin = item.totalUpfrontMargin / termLengthYears;
                updatedValues.termLengthMonths = termLengthYears * 12;

                // It's possible to spread the cost over a period that's not whole years, so work out the monthly cost/price and
                // add it to the months that apply to each year
                let totalMonthlyCost = item.totalUpfrontCost / termLengthMonths;
                let totalMonthlyPrice = item.totalUpfrontPrice / termLengthMonths;

                updatedValues.statsByYear = {}

                // Add stats for each year that is to be spread
                for (let year = 1; year <= termLengthYears; year++) {
                    let month = (year - 1) * 12 + 1;

                    // Initialize year stats to 0 (other than margin, which doesn't change when cost/price are divided so can be set now)
                    updatedValues.statsByYear[year] = {
                        totalRecurringCost: 0,
                        totalRecurringPrice: 0,
                        totalRecurringMargin: updatedValues.totalAnnualMargin,
                        totalUpfrontCost: 0,
                        totalUpfrontPrice: 0,
                        totalUpfrontMargin: 0
                    };

                    // For each month in the year, check if it's within the term length to be spread
                    for(let monthInYear = 1; monthInYear <= 12; monthInYear++) {
                        if (month <= termLengthMonths) {
                            // This month is within the term length to be spread, so add to the year's totals
                            console.log("Adding to year", year, "month", month, "cost", totalMonthlyCost, "price", totalMonthlyPrice);

                            updatedValues.statsByYear[year] = {
                                totalRecurringCost: updatedValues.statsByYear[year].totalRecurringCost + totalMonthlyCost,
                                totalRecurringPrice: updatedValues.statsByYear[year].totalRecurringPrice + totalMonthlyPrice                                
                            };
                        }
                        month++;
                    }
                }
            }
        }
       
        return updatedValues
    }   

    useEffect(() => {
        // remove any recurring portion from the upfront items
        const upfrontQuoteItems = quoteItems.filter(item => item.isUpfront).map(item => ({
            ...item,
            isRecurring: false,
            recurringSalesforceCode: "",
            recurringBusinessCOE: "",
            recurringCost: 0,
            recurringPrice: 0,
            recurringMargin: 0,
            totalContractCost: item.totalUpfrontCost,
            totalContractPrice: item.totalUpfrontPrice,
            totalContractMargin: item.totalUpfrontMargin,
            annualCost: 0,
            annualPrice: 0,
            annualMargin: 0,
            totalAnnualCost: 0,
            totalAnnualPrice: 0,
            totalAnnualMargin: 0,
            totalRecurringCost: 0,
            totalRecurringPrice: 0,
            totalRecurringMargin: 0,

            // Preserve upfront per-year stats but zero out recurring data within each year.
            // Spread items will have their statsByYear rebuilt by updatedValuesForCommercialView.
            statsByYear: Object.fromEntries(Object.entries(item.statsByYear).map(([year, yearStats]) => [year, {
                ...yearStats,

                // Recurring Stats
                totalRecurringCost: 0,
                totalRecurringPrice: 0,
                totalRecurringMargin: 0,
                totalInternalRecurringCost: 0,
                totalExternalRecurringCost: 0,
                totalInternalRecurringPrice: 0,
                totalExternalRecurringPrice: 0,
                totalRecurringRLEC: 0,

                // Full Term Contract Stats (per-year: contract = upfront only since recurring is zeroed)
                totalInternalContractCost: item.isInternal ? yearStats.totalUpfrontCost : 0,
                totalExternalContractCost: !item.isInternal ? yearStats.totalUpfrontCost : 0,
                totalContractCost: yearStats.totalUpfrontCost,
                totalContractPrice: yearStats.totalUpfrontPrice,
                totalContractMargin: yearStats.totalUpfrontMargin,
                totalContractMarginPercentage: item.totalUpfrontMarginPercentage,
                totalContractRLEC: yearStats.totalUpfrontRLEC,
                totalContractRLECPercentage: item.totalUpfrontRLECPercentage,

                // Combined Upfront and Recurring Stats (per-year: combined = upfront only since recurring is zeroed)
                totalInternalUpfrontAndRecurringCost: item.isInternal ? yearStats.totalUpfrontCost : 0,
                totalExternalUpfrontAndRecurringCost: !item.isInternal ? yearStats.totalUpfrontCost : 0,
                totalUpfrontAndRecurringCost: yearStats.totalUpfrontCost,
                totalUpfrontAndRecurringPrice: yearStats.totalUpfrontPrice,
                totalUpfrontAndRecurringMargin: yearStats.totalUpfrontMargin,
                totalUpfrontAndRecurringMarginPercentage: item.totalUpfrontMarginPercentage,
                totalUpfrontAndRecurringRLEC: yearStats.totalUpfrontRLEC,
                totalUpfrontAndRecurringRLECPercentage: item.totalUpfrontRLECPercentage                
            }]))
        }));

        const upfrontQuoteItemsCommercialView = upfrontQuoteItems.map(item => ({  
            ...item,   
            ...updatedValuesForCommercialView(item)
        }));

        // remove any upfront portion from the recurring items
        const recurringQuoteItems = quoteItems.filter(item => item.isRecurring).map(item => ({
            ...item,
            isUpfront: false,
            upfrontSalesforceCode: "",
            upfrontBusinessCOE: "",
            upfrontCost: 0,
            upfrontPrice: 0,
            upfrontMargin: 0,
            totalUpfrontCost: 0,
            totalUpfrontPrice: 0,
            totalUpfrontMargin: 0,
            totalContractCost: item.totalRecurringCost,
            totalContractPrice: item.totalRecurringPrice,
            totalContractMargin: item.totalRecurringMargin,

            // Preserve recurring per-year stats but zero out upfront data within each year
            statsByYear: Object.fromEntries(Object.entries(item.statsByYear).map(([year, yearStats]) => [year, {
                ...yearStats,
                // Upfront Stats
                totalInternalUpfrontCost: 0,
                totalExternalUpfrontCost: 0,
                totalUpfrontCost: 0,
                totalUpfrontPrice: 0,
                totalUpfrontMargin: 0,
                totalUpfrontMarginPercentage: 0,
                totalUpfrontRLEC: 0,
                totalUpfrontRLECPercentage: 0,

                // Full Term Contract Stats (per-year: contract = recurring only since upfront is zeroed)
                totalInternalContractCost: yearStats.totalInternalRecurringCost,
                totalExternalContractCost: yearStats.totalExternalRecurringCost,
                totalContractCost: yearStats.totalRecurringCost,
                totalContractPrice: yearStats.totalRecurringPrice,
                totalContractMargin: yearStats.totalRecurringMargin,
                totalContractMarginPercentage: item.totalRecurringMarginPercentage,
                totalContractRLEC: yearStats.totalRecurringRLEC,
                totalContractRLECPercentage: item.totalRecurringRLECPercentage,

                // Combined Upfront and Recurring Stats (per-year: combined = recurring only since upfront is zeroed)
                totalInternalUpfrontAndRecurringCost: yearStats.totalInternalRecurringCost,
                totalExternalUpfrontAndRecurringCost: yearStats.totalExternalRecurringCost,
                totalUpfrontAndRecurringCost: yearStats.totalRecurringCost,
                totalUpfrontAndRecurringPrice: yearStats.totalRecurringPrice,
                totalUpfrontAndRecurringMargin: yearStats.totalRecurringMargin,
                totalUpfrontAndRecurringMarginPercentage: item.totalRecurringMarginPercentage,
                totalUpfrontAndRecurringRLEC: yearStats.totalRecurringRLEC,
                totalUpfrontAndRecurringRLECPercentage: item.totalRecurringRLECPercentage                
            }])) 
        }));


        setQuoteItemsCommercialView([...upfrontQuoteItemsCommercialView, ...recurringQuoteItems]);
    }, [quoteItems]);

    useEffect(() => {
        setQuoteItemsWithLinkedData((profitAndLossView ? quoteItemsCommercialView : quoteItems).map(item => ({  
            ...item,   
            quoteGroupDescription: item.quoteModuleGuid ? item.quoteGroupDescription + " (Module) " :  item.quoteGroupDescription,     
            itemLookupType: itemLookupTypes?.find(x => x.id === item.itemLookupTypeId)?.itemLookupTypeName || "",
            supplierName: suppliers?.find(supplier => supplier.id === item.supplierId)?.supplierName || ""
        })))
    }, [profitAndLossView, quoteItemsCommercialView, quoteItems, itemLookupTypes, suppliers]);

    useEffect(() => {
        console.log("exportData changed", exportData);
    }, [exportData]);

    const exportFile = async () => {
        setIsDownloadingExport(true);
        const { fileBytes, fileName } = await exportDataAsExcel(exportData);
        if (!fileBytes) {
            toast.info("No file returned.");
            setIsDownloadingExport(false);
            return;
        }

        const byteChars = atob(fileBytes);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
        const blob = new Uint8Array(byteNumbers);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([blob], { type: 'application/octet-stream' }));
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsDownloadingExport(false);
    }

    return (
        <>
            <Row>
                <Col>
                    <Card>
                        <CardHeader>
                            <Row>
                                <Col style={{ display: "inline-flex" }}>
                                    {["General", "Financial", "System", "Salesforce"].map((tab) => (
                                        <h6
                                            key={tab}
                                            className={
                                                currentActiveMainTab === tab
                                                    ? "custom-nav-selected custom-nav m-3"
                                                    : "custom-nav m-3"
                                            }
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setCurrentActiveMainTab(tab)}
                                        >
                                            {tab}
                                        </h6>
                                    ))}
                                </Col>
                                <Col sm="auto" className="text-end">
                                    <FormGroup switch>
                                        <Input
                                            type="switch"
                                            role="switch"
                                            checked={profitAndLossView}
                                            onChange={() => setProfitAndLossView(!profitAndLossView)}
                                        />
                                        <Label check>Show Profit & Loss View</Label>
                                    </FormGroup>
                                    <Button 
                                        color="primary" 
                                        className="mt-3" 
                                        disabled={isDownloadingExport}
                                        onClick={() => {
                                            exportFile();
                                    }}>
                                        {isDownloadingExport ? "Exporting..." : "Export Data"}
                                    </Button>
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody>
                            <QuoteOverviewFinancial
                                quoteItems={quoteItemsWithLinkedData}
                                setExportData={setExportData}
                                isVisible={currentActiveMainTab === "Financial"}
                            />
                            <QuoteOverviewGeneral
                                quoteItems={quoteItemsWithLinkedData}
                                setExportData={setExportData}
                                isVisible={currentActiveMainTab === "General"}
                            />

                            {currentActiveMainTab === "System" && (
                                <QuoteOverviewSystem quoteItems={quoteItemsWithLinkedData} />
                            )}
                            {currentActiveMainTab === "Salesforce" && (quote?.isActive && quoteRevision?.isMain) && (
                                <QuoteOverviewSalesforceTable salesforceFields={salesforceFields} />
                            )}
                            {currentActiveMainTab === "Salesforce" && !quote?.isActive && (
                                <span>This is not the active quote for this opportunity. So the Salesforce sync is disabled and, therefore, Salesforce fields will not be shown here.</span>
                            )}
                            {currentActiveMainTab === "Salesforce" && (quote?.isActive && !quoteRevision?.isMain) && (
                                <span>This is not the main revision of this quote. So the Salesforce sync is disabled and, therefore, Salesforce fields will not be shown here.</span>
                            )}

                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>

    );
}
export default QuoteOverview;